import React, { useMemo, useState } from 'react';
import { FlaskConical } from 'lucide-react';
import { Button } from '@components/Button';
import { Card, CardContent, CardHeader, Badge } from '@components/Card';
import { paymentRoutingService } from '@services/paymentRoutingService';
import type {
  CarrierServiceOption,
  RoutingPaymentKind,
  SimulateRoutingResult,
} from '@/types/paymentRouting';
import { formatCurrencyBRL } from '@pages/financial/financialUtils';
import { capDraftToCents, capInputDisplay, capInputFromTyped, groupCarrierServices } from './weightedRoutingUtils';

interface RoutingSimulatorCardProps {
  carrierServices: CarrierServiceOption[];
}

const yesNo = (ok: boolean) => (ok ? 'Sim' : 'Não');

/**
 * Simulador do roteamento: pergunta ao PaymentHub (via ERPApi) para qual empresa um pagamento
 * iria agora, com a configuração salva e o recebido do mês. Não cobra nem registra decisão.
 * Simula a configuração SALVA — alterações ainda não salvas na tela não entram.
 */
export const RoutingSimulatorCard: React.FC<RoutingSimulatorCardProps> = ({ carrierServices }) => {
  const [amount, setAmount] = useState('');
  const [carrier, setCarrier] = useState(''); // "providerId:serviceId" | ''
  const [channel, setChannel] = useState(1);
  const [city, setCity] = useState('');
  const [uf, setUf] = useState('');
  const [kind, setKind] = useState<RoutingPaymentKind>('pix');
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<SimulateRoutingResult | null>(null);

  const groups = useMemo(() => groupCarrierServices(carrierServices), [carrierServices]);

  const run = async () => {
    const cents = capDraftToCents(amount);
    if (cents == null || cents <= 0) {
      setError('Informe o valor do pagamento.');
      return;
    }
    const [pid, sid] = carrier ? carrier.split(':').map(Number) : [null, null];
    try {
      setRunning(true);
      setError(null);
      const res = await paymentRoutingService.simulate({
        amount: cents / 100,
        sales_channel_id: channel,
        shipping_provider_id: pid,
        shipping_provider_service_id: sid,
        delivery_city: city.trim(),
        delivery_state: uf.trim().toUpperCase(),
        payment_kind: kind,
      });
      setResult(res);
    } catch (err) {
      setResult(null);
      setError(err instanceof Error ? err.message : 'Erro ao simular');
    } finally {
      setRunning(false);
    }
  };

  const inputCls = 'w-full rounded-lg border border-slate-300 px-3 py-2 text-sm';

  return (
    <Card>
      <CardHeader
        title={
          <span className="flex items-center gap-2">
            <FlaskConical size={16} className="text-emerald-600" />
            Simulador
          </span>
        }
        subtitle="Usa a configuração salva e o recebido do mês. Nada é cobrado nem registrado."
      />
      <CardContent>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3 items-end">
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Valor (R$)</label>
            <input
              type="text"
              inputMode="numeric"
              className={inputCls}
              value={capInputDisplay(amount)}
              onChange={e => setAmount(capInputFromTyped(e.target.value))}
            />
          </div>
          <div className="lg:col-span-2">
            <label className="block text-xs font-medium text-slate-600 mb-1">Transportadora escolhida</label>
            <select className={inputCls} value={carrier} onChange={e => setCarrier(e.target.value)}>
              <option value="">Nenhuma / retirada</option>
              {groups.map(g => (
                <optgroup key={g.provider_id} label={g.provider_name}>
                  {g.services.map(s => (
                    <option key={s.provider_service_id} value={`${s.provider_id}:${s.provider_service_id}`}>
                      {s.provider_service_name}{s.customer_label ? ` (${s.customer_label})` : ''}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Canal</label>
            <select className={inputCls} value={channel} onChange={e => setChannel(Number(e.target.value))}>
              <option value={1}>Site</option>
              <option value={2}>CRM (proposta)</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Meio</label>
            <select className={inputCls} value={kind} onChange={e => setKind(e.target.value as RoutingPaymentKind)}>
              <option value="pix">PIX</option>
              <option value="card">Cartão</option>
            </select>
          </div>
          <div className="flex gap-2">
            <div className="flex-1">
              <label className="block text-xs font-medium text-slate-600 mb-1">Cidade</label>
              <input type="text" className={inputCls} value={city} onChange={e => setCity(e.target.value)} />
            </div>
            <div className="w-16">
              <label className="block text-xs font-medium text-slate-600 mb-1">UF</label>
              <input
                type="text"
                maxLength={2}
                className={`${inputCls} uppercase`}
                value={uf}
                onChange={e => setUf(e.target.value.toUpperCase())}
              />
            </div>
          </div>
        </div>
        <div className="mt-3">
          <Button size="sm" onClick={run} loading={running} icon={<FlaskConical size={14} />}>
            Simular
          </Button>
        </div>

        {error && <p className="mt-3 text-sm text-red-600">{error}</p>}

        {result && (
          <div className="mt-4 space-y-3">
            {result.config_error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
                Configuração inválida: {result.config_error}
              </div>
            )}
            {result.message && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
                {result.message}
              </div>
            )}
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3">
              <p className="text-sm text-slate-700">
                Vai para{' '}
                <strong className="text-emerald-800">
                  {result.company_id ? result.company_name || `Empresa #${result.company_id}` : 'nenhuma empresa'}
                </strong>
                {result.forced && <Badge variant="warning" className="ml-2">Forçado</Badge>}
              </p>
              <p className="text-xs text-slate-600 mt-1">{result.reason}</p>
            </div>
            {result.companies.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-50 uppercase tracking-wide text-slate-500">
                      <th className="px-3 py-2 text-left">Empresa</th>
                      <th className="px-3 py-2 text-right">Peso</th>
                      <th className="px-3 py-2 text-right">Recebido</th>
                      <th className="px-3 py-2 text-right">Teto</th>
                      <th className="px-3 py-2 text-right">Recebido ÷ peso</th>
                      <th className="px-3 py-2 text-center">Ativa</th>
                      <th className="px-3 py-2 text-center">Adquirente</th>
                      <th className="px-3 py-2 text-center">Cidade</th>
                      <th className="px-3 py-2 text-center">Transportadora</th>
                      <th className="px-3 py-2 text-center">Cabe no teto</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.companies.map(c => (
                      <tr
                        key={c.company_id}
                        className={`border-b border-slate-100 ${c.company_id === result.company_id ? 'bg-emerald-50/60 font-medium' : ''}`}
                      >
                        <td className="px-3 py-2">{c.company_name || `#${c.company_id}`}</td>
                        <td className="px-3 py-2 text-right">{c.weight}</td>
                        <td className="px-3 py-2 text-right">{formatCurrencyBRL(c.captured)}</td>
                        <td className="px-3 py-2 text-right">{c.cap > 0 ? formatCurrencyBRL(c.cap) : 'sem teto'}</td>
                        <td className="px-3 py-2 text-right">{formatCurrencyBRL(c.ratio)}</td>
                        <td className="px-3 py-2 text-center">{yesNo(c.enabled)}</td>
                        <td className="px-3 py-2 text-center">{yesNo(c.acquirer_ok)}</td>
                        <td className="px-3 py-2 text-center">{yesNo(c.city_ok)}</td>
                        <td className="px-3 py-2 text-center">{c.carrier_blocked ? 'Bloqueada' : 'Aceita'}</td>
                        <td className="px-3 py-2 text-center">{yesNo(c.fits_cap)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
